import config from '../utils/config';
import { appLocalStorage } from '../utils/storage';
import { removeUser } from '../utils/func';
import { cleanupDeletedOwner, retryPendingDeletionCleanup } from './cleanup';
import { abortable } from '../utils/abortable';
import { isAppActive, subscribeAppActivity } from '../utils/appActivity';

const pending = new Map();

/** A 401/expired session is not erasure evidence. This endpoint accepts only a
 * previously recorded deleted session and returns no former private content. */
export function reconcileDeletedSession(token) {
  if (typeof token !== 'string' || !token) return Promise.resolve(null);
  if (pending.has(token)) return pending.get(token);
  const operation = (async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      const aborted = new DOMException('Account status check aborted', 'AbortError');
      const response = await abortable(fetch(`${(process.env.REACT_APP_API_URL || '').replace(/\/$/, '')}/account-deletions/session`, {
        method: 'GET', headers: { [config.token]: token }, credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer', signal: controller.signal,
      }), controller.signal, aborted);
      if (!response.ok) return null;
      const evidence = await abortable(response.json(), controller.signal, aborted);
      const proof = evidence?.deletion;
      if (evidence.success !== true || evidence.code !== 'ACCOUNT_DELETED' || !Number.isSafeInteger(proof?.user_id)
        || proof.user_id < 1 || !Number.isInteger(proof.generation) || proof.generation < 1
        || !Number.isFinite(Date.parse(proof.deleted_at))) return null;
      try { await cleanupDeletedOwner(proof.user_id, token); } catch { /* Durable tombstone retries local cleanup. Remote closure is final. */ }
      if (appLocalStorage.getItem(config.token) === token) removeUser({ preserveAuthAttempts: false });
      return proof;
    } catch { return null; }
    finally { clearTimeout(timer); }
  })().finally(() => { if (pending.get(token) === operation) pending.delete(token); });
  pending.set(token, operation);
  return operation;
}

// Install once at the app boundary. Active sessions receive no deletion proof;
// returning from iOS suspension or another tab also retries interrupted erasure.
export function subscribeDeletionReconciliation() {
  let lastToken, lastCheck = 0;
  const check = () => {
    if (!isAppActive()) return;
    void retryPendingDeletionCleanup();
    const token = appLocalStorage.getItem(config.token);
    if (!token || (token === lastToken && Date.now() - lastCheck < 5000)) return;
    lastToken = token; lastCheck = Date.now();
    void reconcileDeletedSession(token);
  };
  window.addEventListener('pageshow', check);
  window.addEventListener('focus', check);
  window.addEventListener('online', check);
  document.addEventListener('visibilitychange', check);
  const unsubscribeActivity = subscribeAppActivity(({ active }) => { if (active) check(); });
  check();
  return () => {
    window.removeEventListener('pageshow', check);
    window.removeEventListener('focus', check);
    window.removeEventListener('online', check);
    document.removeEventListener('visibilitychange', check);
    unsubscribeActivity();
  };
}
